/**
 * Candidate Preview Strategy
 * 
 * This strategy handles the candidate preview phase, which shows sample matching candidates
 * to the user one at a time and asks for their reaction. This phase comes after the title
 * phase and before the company phase.
 */

import { runAIMatches, fetchProfiles } from '../api';
import React, { useState } from 'react';
import CandidatePreview from '../../components/CandidatePreview';
import generateEnhancedGuidance from './enhancedGuidance';
import { createBaseStrategy, StrategyPhase } from './baseStrategy';

/**
 * Fetch candidate profiles based on the current requirements
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Optional decision object that may contain handleRefreshResults
 * @returns {Object} Object containing AI matches result and candidate profiles
 */
const fetchCandidateProfiles = async (messages, setMessages, decision = null) => {
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nFetching candidate profiles...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  // First get the regular AI matches to extract requirements
  const regularMatches = await runAIMatches(messages);
  
  // Update match results in the sidebar if handleRefreshResults is provided
  if (decision && decision.handleRefreshResults) {
    console.log('Updating match results in sidebar with handleRefreshResults');
    decision.handleRefreshResults(messages, regularMatches);
  }
  
  if (!regularMatches) {
    throw new Error('Failed to get AI matches');
  }
  
  // Extract requirements from regularMatches
  const requirements =
    regularMatches.search_gen_response &&
    regularMatches.search_gen_response.icp_requirements
      ? regularMatches.search_gen_response.icp_requirements
      : [];
  
  // Fetch profiles using the requirements
  try {
    // Use the centralized fetchMatches function with count: 5 for candidate preview
    const matchesData = await runAIMatches(messages, { count: 5 });
    
    const prids = matchesData?.query_results?.map(result => result.profile_id) || [];
    if (prids.length > 0) {
      console.log('Profile IDs:', prids);
      const profilesData = await fetchProfiles(prids);
      
      // Combine the results
      return {
        aiMatchesResult: regularMatches,
        candidateProfiles: profilesData
      };
    } else {
      console.warn('No profile IDs returned from matches.');
      return {
        aiMatchesResult: regularMatches,
        candidateProfiles: {}
      };
    }
  } catch (error) {
    console.error('Error fetching candidate profiles:', error);
    return {
      aiMatchesResult: regularMatches,
      candidateProfiles: {}
    };
  }
};

/**
 * Handler for the init phase of the candidate preview strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Candidate preview strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing candidate preview...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Fetch candidate profiles
    const { aiMatchesResult, candidateProfiles } = await fetchCandidateProfiles(messages, setMessages, decision);
    
    // Check if we have candidate profiles
    if (candidateProfiles && Object.keys(candidateProfiles).length > 0) {
      // Add an AI message explaining the candidate preview phase
      if (setMessages) {
        setMessages(prev => {
          // Create a new AI message
          const newMessage = {
            role: 'AI',
            content: "Based on the job requirements we've discussed, I've found some potential candidates that match your criteria. Let's review them one by one, and you can let me know what you think of each candidate."
          };
          
          // Add the new message to the messages array
          return [...prev.filter(msg => msg.content !== 'Typing...'), newMessage];
        });
      }
      
      // Return a decision that will render the candidate preview component
      return {
        ...decision,
        actionType: 'RENDER_CANDIDATE_PREVIEW',
        component: CandidatePreview,
        aiMatchesResult,
        candidateProfiles,
        strategyPhase: StrategyPhase.SUGGESTIONS,
        componentProps: {
          candidate: Object.values(candidateProfiles)[0],
          profileId: Object.keys(candidateProfiles)[0],
          currentIndex: 1,
          totalCandidates: Object.keys(candidateProfiles).length,
          onLike: () => {
            // Add a user message indicating they liked the candidate
            if (setMessages) {
              setMessages(prev => {
                const candidateName = Object.values(candidateProfiles)[0]?.candidate?.full_name || 'this candidate';
                const newMessage = {
                  role: 'User',
                  content: `I like ${candidateName}. Their profile looks promising.`
                };
                return [...prev, newMessage];
              });
            }
            
            // Move to the next candidate or finish if this is the last one
            if (Object.keys(candidateProfiles).length > 1) {
              return {
                ...decision,
                actionType: 'RENDER_CANDIDATE_PREVIEW',
                candidateIndex: 1
              };
            } else {
              return {
                ...decision,
                actionType: 'ADVANCE_TOPIC'
              };
            }
          },
          onDislike: () => {
            // Add a user message indicating they disliked the candidate
            if (setMessages) {
              setMessages(prev => {
                const candidateName = Object.values(candidateProfiles)[0]?.candidate?.full_name || 'this candidate';
                const newMessage = {
                  role: 'User',
                  content: `I don't think ${candidateName} is a good fit for this role.`
                };
                return [...prev, newMessage];
              });
            }
            
            // Move to the next candidate or finish if this is the last one
            if (Object.keys(candidateProfiles).length > 1) {
              return {
                ...decision,
                actionType: 'RENDER_CANDIDATE_PREVIEW',
                candidateIndex: 1
              };
            } else {
              return {
                ...decision,
                actionType: 'ADVANCE_TOPIC'
              };
            }
          },
          onNext: () => {
            // Move to the next candidate
            return {
              ...decision,
              actionType: 'RENDER_CANDIDATE_PREVIEW',
              candidateIndex: 1
            };
          },
          onFinish: () => {
            // Add a user message indicating they're done reviewing candidates
            if (setMessages) {
              setMessages(prev => {
                const newMessage = {
                  role: 'User',
                  content: "I've reviewed the candidates. Let's move on to the next topic."
                };
                return [...prev, newMessage];
              });
            }
            
            // Advance to the next topic
            return {
              ...decision,
              actionType: 'ADVANCE_TOPIC'
            };
          }
        }
      };
    } else {
      // Fall back to the default behavior if we don't have candidate profiles
      const defaultGuidance = `Based on the job requirements we've discussed, I'll help you find candidates that match your criteria. Let's move on to discussing the company and industry context to further refine our search.`;
      
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: defaultGuidance,
        strategyPhase: StrategyPhase.SUGGESTIONS
      };
    }
  } catch (error) {
    console.error('Error fetching candidate profiles:', error);
    
    // Fall back to the default behavior if there's an error
    const defaultGuidance = `Based on the job requirements we've discussed, I'll help you find candidates that match your criteria. Let's move on to discussing the company and industry context to further refine our search.`;
    
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: defaultGuidance,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
  }
};

/**
 * Handler for the suggestions phase of the candidate preview strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Candidate preview strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating candidate suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Check if we have a candidateIndex in the decision
    if (decision.candidateIndex !== undefined) {
      // Fetch candidate profiles if we don't have them
      let candidateProfiles = decision.candidateProfiles;
      let aiMatchesResult = decision.aiMatchesResult;
      
      if (!candidateProfiles || Object.keys(candidateProfiles).length === 0) {
        const result = await fetchCandidateProfiles(messages, setMessages, decision);
        candidateProfiles = result.candidateProfiles;
        aiMatchesResult = result.aiMatchesResult;
      }
      
      // Get the candidate at the specified index
      const candidateKeys = Object.keys(candidateProfiles);
      const candidateIndex = decision.candidateIndex;
      
      if (candidateIndex < candidateKeys.length) {
        const profileId = candidateKeys[candidateIndex];
        const candidate = candidateProfiles[profileId];
        
        // Add an AI message for the next candidate
        if (setMessages) {
          setMessages(prev => {
            // Create a new AI message
            const newMessage = {
              role: 'AI',
              content: `Here's another candidate that matches your criteria. Let me know what you think.`
            };
            
            // Add the new message to the messages array
            return [...prev.filter(msg => msg.content !== 'Typing...'), newMessage];
          });
        }
        
        // Return a decision that will render the candidate preview component
        return {
          ...decision,
          actionType: 'RENDER_CANDIDATE_PREVIEW',
          component: CandidatePreview,
          aiMatchesResult,
          candidateProfiles,
          strategyPhase: StrategyPhase.CONVERSATION,
          componentProps: {
            candidate,
            profileId,
            currentIndex: candidateIndex + 1,
            totalCandidates: candidateKeys.length,
            onLike: () => {
              // Add a user message indicating they liked the candidate
              if (setMessages) {
                setMessages(prev => {
                  const candidateName = candidate?.candidate?.full_name || 'this candidate';
                  const newMessage = {
                    role: 'User',
                    content: `I like ${candidateName}. Their profile looks promising.`
                  };
                  return [...prev, newMessage];
                });
              }
              
              // Move to the next candidate or finish if this is the last one
              if (candidateIndex + 1 < candidateKeys.length) {
                return {
                  ...decision,
                  actionType: 'RENDER_CANDIDATE_PREVIEW',
                  candidateIndex: candidateIndex + 1
                };
              } else {
                return {
                  ...decision,
                  actionType: 'ADVANCE_TOPIC'
                };
              }
            },
            onDislike: () => {
              // Add a user message indicating they disliked the candidate
              if (setMessages) {
                setMessages(prev => {
                  const candidateName = candidate?.candidate?.full_name || 'this candidate';
                  const newMessage = {
                    role: 'User',
                    content: `I don't think ${candidateName} is a good fit for this role.`
                  };
                  return [...prev, newMessage];
                });
              }
              
              // Move to the next candidate or finish if this is the last one
              if (candidateIndex + 1 < candidateKeys.length) {
                return {
                  ...decision,
                  actionType: 'RENDER_CANDIDATE_PREVIEW',
                  candidateIndex: candidateIndex + 1
                };
              } else {
                return {
                  ...decision,
                  actionType: 'ADVANCE_TOPIC'
                };
              }
            },
            onNext: () => {
              // Move to the next candidate
              return {
                ...decision,
                actionType: 'RENDER_CANDIDATE_PREVIEW',
                candidateIndex: candidateIndex + 1
              };
            },
            onFinish: () => {
              // Add a user message indicating they're done reviewing candidates
              if (setMessages) {
                setMessages(prev => {
                  const newMessage = {
                    role: 'User',
                    content: "I've reviewed the candidates. Let's move on to the next topic."
                  };
                  return [...prev, newMessage];
                });
              }
              
              // Advance to the next topic
              return {
                ...decision,
                actionType: 'ADVANCE_TOPIC'
              };
            }
          }
        };
      } else {
        // We've shown all candidates, advance to the next topic
        // Add a final AI message summarizing the candidate preview phase
        if (setMessages) {
          setMessages(prev => {
            // Create a new AI message
            const newMessage = {
              role: 'AI',
              content: "That's all the candidates I have for now. Let's move on to discussing the company and industry context to further refine our search."
            };
            
            // Add the new message to the messages array
            return [...prev.filter(msg => msg.content !== 'Typing...'), newMessage];
          });
        }
        
        // Advance to the next topic
        return {
          ...decision,
          actionType: 'ADVANCE_TOPIC'
        };
      }
    } else {
      // Generate enhanced agent guidance with Findem Magic explanations
      const enhancedGuidance = await generateEnhancedGuidance(
        decision.aiMatchesResult, 
        decision, 
        'candidate_preview'
      );
      
      // Update decision with AI matches result and enhanced guidance
      return {
        ...decision,
        actionType: 'SEND_WITH_GUIDANCE',
        agentGuidance: enhancedGuidance,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error generating candidate suggestions:', error);
    
    // Fall back to conversation phase without suggestions
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the candidate preview strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Candidate preview strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about candidates...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Check if we have a candidateIndex in the decision
    if (decision.candidateIndex !== undefined) {
      // Handle the same way as in suggestionsPhaseHandler
      return await suggestionsPhaseHandler(decision, messages, setMessages);
    } else {
      // Generate specific guidance for the candidate preview conversation phase
      const candidateConversationGuidance = `
IMPORTANT: The current topic is CANDIDATE PREVIEW. Focus on discussing the candidates that match the job requirements.

1. First, acknowledge the user's feedback on the candidates they've seen:
   - If they liked a candidate, ask what specific qualities they found appealing
   - If they disliked a candidate, ask what aspects weren't a good fit

2. Then, explore other aspects of candidate preferences that might be relevant:
   - Are there specific skills or experiences they're looking for that weren't represented?
   - Is there a particular background or industry experience that would be valuable?

3. After gathering sufficient information, summarize their candidate preferences:
   - "Based on your feedback, it seems you're looking for candidates with [summarize preferences]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Now that we've reviewed some potential candidates, shall we move on to discussing company and industry context?"
   - "Have I captured your candidate preferences correctly? If yes, we can proceed to the next area."

DO NOT change the topic until the user has EXPLICITLY confirmed they're ready to move on from the candidate discussion.
`;
  
      // In the conversation phase, we pass the user's message to the backend with specific guidance
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        agentGuidance: candidateConversationGuidance,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  } catch (error) {
    console.error('Error in candidate preview conversation phase:', error);
    
    // Generate specific guidance for the candidate preview conversation phase
    const candidateConversationGuidance = `
IMPORTANT: The current topic is CANDIDATE PREVIEW. Focus on discussing the candidates that match the job requirements.

1. First, acknowledge the user's feedback on the candidates they've seen:
   - If they liked a candidate, ask what specific qualities they found appealing
   - If they disliked a candidate, ask what aspects weren't a good fit

2. Then, explore other aspects of candidate preferences that might be relevant:
   - Are there specific skills or experiences they're looking for that weren't represented?
   - Is there a particular background or industry experience that would be valuable?

3. After gathering sufficient information, summarize their candidate preferences:
   - "Based on your feedback, it seems you're looking for candidates with [summarize preferences]..."

4. Finally, EXPLICITLY ask if they're ready to move on to the next topic:
   - "Now that we've reviewed some potential candidates, shall we move on to discussing company and industry context?"
   - "Have I captured your candidate preferences correctly? If yes, we can proceed to the next area."

DO NOT change the topic until the user has EXPLICITLY confirmed they're ready to move on from the candidate discussion.
`;
    
    // Fall back to conversation phase without suggestions
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: candidateConversationGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Candidate preview strategy using the phase-based approach
 */
const candidatePreviewStrategy = createBaseStrategy({
  topicName: 'candidate_preview',
  initPhaseHandler,
  suggestionsPhaseHandler,
  conversationPhaseHandler
});

export default candidatePreviewStrategy;
